import { useQuery } from '@tanstack/react-query';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { IF_SWAPS_OPTIONS, filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { todayDateString } from '@/api/services/riskDashboard';
import type { ClientProfitabilityFilterInput } from '@/api/types/riskDashboard';

export interface CpFilterState {
  startDate: string;
  endDate: string;
  mifidClassification: string | null;
  executionVenue: string | null;
  group: string | null;
  groupText: string | null;
  groupProfile: string | null;
  dataSourceId: number | null;
  ifSwaps: string | null;
  countryId: number | null;
  portfolioId: number | null;
  login: number | null;
  uniqueId: string | null;
  byUniqueId: boolean;
  liveData: boolean;
  showLp: boolean;
}

export function defaultCpFilters(): CpFilterState {
  const today = todayDateString();
  return {
    startDate: today,
    endDate: today,
    mifidClassification: null,
    executionVenue: null,
    group: null,
    groupText: null,
    groupProfile: null,
    dataSourceId: null,
    ifSwaps: null,
    countryId: null,
    portfolioId: null,
    login: null,
    uniqueId: null,
    byUniqueId: false,
    liveData: false,
    showLp: false,
  };
}

export function toCpInput(f: CpFilterState, requestGuid: string): ClientProfitabilityFilterInput {
  // Portfolio exclusivity (index.js DISABLEABLE_FILTERS): a selected portfolio
  // disables + clears the other scoping filters, so none of them are sent.
  const portfolioLocked = f.portfolioId != null;
  return {
    startDate: f.startDate,
    endDate: f.endDate,
    mifidClassification: portfolioLocked ? null : ((f.mifidClassification as ClientProfitabilityFilterInput['mifidClassification']) ?? null),
    executionVenue: portfolioLocked ? null : ((f.executionVenue as ClientProfitabilityFilterInput['executionVenue']) ?? null),
    group: portfolioLocked ? null : f.group,
    groupText: portfolioLocked ? null : f.groupText,
    groupProfile: portfolioLocked ? null : f.groupProfile,
    dataSourceId: portfolioLocked ? null : f.dataSourceId,
    ifSwaps: portfolioLocked ? null : f.ifSwaps,
    countryId: portfolioLocked ? null : f.countryId,
    portfolioId: f.portfolioId,
    login: portfolioLocked || f.byUniqueId ? null : f.login,
    uniqueId: f.byUniqueId ? f.uniqueId : null,
    byUniqueId: f.byUniqueId,
    liveData: f.liveData,
    showLp: f.showLp,
    requestGuid,
    maxResultCount: 50000,
    skipCount: 0,
  };
}

interface Props {
  draft: CpFilterState;
  setDraft: React.Dispatch<React.SetStateAction<CpFilterState>>;
  onFetch: () => void;
  onModeToggle: (key: 'byUniqueId' | 'liveData' | 'showLp', value: boolean) => void;
  fetching: boolean;
  statusSlot?: React.ReactNode;
}

export function ClientProfitabilityFilterBar({ draft, setDraft, onFetch, onModeToggle, fetching, statusSlot }: Props) {
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getEnabledConnectors, staleTime: 5 * 60_000 });
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const venues = useQuery({ queryKey: ['rd-filter', 'venues'], queryFn: filterDataApi.getExecutionVenues, staleTime: 5 * 60_000 });
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });
  const groupProfiles = useQuery({ queryKey: ['rd-filter', 'groupProfiles'], queryFn: filterDataApi.getGroupProfiles, staleTime: 5 * 60_000 });

  const today = todayDateString();
  const str = (v: string | number | null) => (v == null ? '' : String(v));
  // Portfolio exclusivity: picking a portfolio greys + disables the other scoping
  // filters AND clears their values (index.js handlePortfolioChange → clearSelectFilter
  // on DISABLEABLE_FILTERS) so they are not sent in the request.
  const lock = draft.portfolioId != null ? 'pe-none opacity-50' : '';
  const uid = draft.byUniqueId;
  const lk = (node: React.ReactNode) => <div className={lock}>{node}</div>;

  const onPortfolioChange = (v: string | null) => {
    const id = v ? Number(v) : null;
    setDraft((f) =>
      id != null
        ? {
            ...f,
            portfolioId: id,
            mifidClassification: null,
            executionVenue: null,
            group: null,
            groupText: null,
            groupProfile: null,
            dataSourceId: null,
            ifSwaps: null,
            countryId: null,
            login: null,
          }
        : { ...f, portfolioId: null },
    );
  };

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Range</label>
          <DateRangePicker
            mode="range"

            maxDate={today}
            value={{ start: draft.startDate, end: draft.endDate }}
            onChange={(v) => {
              const end = v.end;
              const start = v.start > end ? end : v.start;
              setDraft((f) => ({ ...f, startDate: start, endDate: end, liveData: end === today }));
            }}
          />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    lk(<Select2 label="MiFID Classification" allowClear placeholder="All" value={str(draft.mifidClassification)} options={mifid.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, mifidClassification: v || null }))} />),
    lk(<Select2 label="Execution Venue" allowClear placeholder="All" value={str(draft.executionVenue)} options={venues.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, executionVenue: v || null }))} />),
    lk(
      <>
        <label className="form-label">Group</label>
        <Select2Async placeholder="All" value={str(draft.group) || null} fetch={filterDataPaged.groups} onChange={(v) => setDraft((f) => ({ ...f, group: v || null }))} />
      </>,
    ),
    lk(
      <>
        <label className="form-label">Group Text</label>
        <input className="form-control" placeholder="S*, *abc*, !SPOT" value={draft.groupText ?? ''} onChange={(e) => setDraft((f) => ({ ...f, groupText: e.target.value || null }))} />
      </>,
    ),
    lk(<Select2 label="Group Profile" allowClear placeholder="All" value={str(draft.groupProfile)} options={groupProfiles.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, groupProfile: v || null }))} />),
    ...(!uid ? [lk(<Select2 label="Connector" allowClear placeholder="All" value={str(draft.dataSourceId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, dataSourceId: v ? Number(v) : null }))} />)] : []),
    lk(<Select2 label="If Swaps" allowClear placeholder="All" value={str(draft.ifSwaps)} options={IF_SWAPS_OPTIONS} onChange={(v) => setDraft((f) => ({ ...f, ifSwaps: v || null }))} />),
    lk(
      <>
        <label className="form-label">Country</label>
        <Select2Async placeholder="All" value={str(draft.countryId) || null} fetch={filterDataPaged.countries} onChange={(v) => setDraft((f) => ({ ...f, countryId: v ? Number(v) : null }))} />
      </>,
    ),
    ...(!uid
      ? [<Select2 key="portfolio" label="Portfolio Profile" allowClear placeholder="All" value={str(draft.portfolioId)} options={portfolios.data ?? []} onChange={onPortfolioChange} />]
      : []),
    uid ? (
      <div key="uid">
        <label className="form-label">Unique ID</label>
        <input className="form-control" value={draft.uniqueId ?? ''} onChange={(e) => setDraft((f) => ({ ...f, uniqueId: e.target.value || null }))} />
      </div>
    ) : (
      lk(
        <>
          <label className="form-label">Login</label>
          <input className="form-control" inputMode="numeric" value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} />
        </>,
      )
    ),
  ];

  return (
    <FilterBar
      fields={fields}
      visibleCount={4}
      modes={
        <>
          <Switch label="By Unique ID" on={draft.byUniqueId} onToggle={(c) => onModeToggle('byUniqueId', c)} />
          <Switch label="Live Data" on={draft.liveData} onToggle={(c) => onModeToggle('liveData', c)} />
          <Switch label="Show LP" on={draft.showLp} onToggle={(c) => onModeToggle('showLp', c)} />
        </>
      }
      actions={
        <button type="button" className="btn btn-primary btn-sm" onClick={onFetch} disabled={fetching}>
          {fetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}
          Fetch
        </button>
      }
      status={statusSlot}
    />
  );
}

function Switch({ label, on, onToggle }: { label: string; on: boolean; onToggle: (c: boolean) => void }) {
  const id = `cp-switch-${label.replace(/\s+/g, '')}`;
  return (
    <div className="form-check form-switch mb-0">
      <input className="form-check-input" type="checkbox" role="switch" id={id} checked={on} onChange={(e) => onToggle(e.target.checked)} />
      <label className="form-check-label small" htmlFor={id}>{label}</label>
    </div>
  );
}
